<template>
	<Page animate class="offer-trial">
		<template #content>
			<SafeAreaTop
				@close="close"
				@back="handleBackButtonClick"
				theme="white"
				:show-close="!isOfferTrailHasForcePaywall"
				:show-back-button="isOfferTrailHasForcePaywall"
			>
				<div class="container" :class="[isOfferTrailHasForcePaywall && 'with-back-button']">
					<div class="title" v-html="offerTrialConfig.pageTitle"></div>
					<div class="trial-image-wrapper">
						<OfferTrial7daysImage class="trial-image" />
					</div>
					<div class="description">
						<div class="description-trial">7 day free trial</div>
						<div class="description-separator">•</div>
						<div class="description-price">{{ activeProduct.price }}</div>
					</div>
					<div class="points">
						<div class="point" v-for="item in points" :key="item.id">
							<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
								<circle cx="12" cy="12" r="12" fill="#ECEAF5" />
								<path
									d="M17.1005 7.7998L12.149 15.7772C11.9166 16.1517 11.4002 16.221 11.0773 15.9211L7.50049 12.5998"
									stroke="#7D8BF7"
									stroke-width="2.10119"
									stroke-linecap="round"
								/>
							</svg>
							<div class="point-text">{{ item.text }}</div>
						</div>
					</div>
				</div>
			</SafeAreaTop>
			<Prompt
				:isOpen="showPaywallCancelPrompt"
				:title="t('cancel_popup.title')"
				:text="t('cancel_popup.body', { store: !isAndroid ? 'iTunes Store' : 'Play Store' })"
				:action="t('cancel_popup.action')"
				@onClose="onClosePaywallCancelPrompt"
				@onAction="onActionPaywallCancelPrompt"
			/>
		</template>
		<template #footer>
			<div class="container">
				<div class="price">{{ activeProduct.subtitle }}. No commitment. Cancel anytime</div>
				<p-button @click.prevent="handleSubmit" label="" :loading="isLoading" cls="btn-primary btn-block">
					{{ offerTrialConfig.continueButtonText }}
				</p-button>
				<product-terms :terms="activeProduct.terms" />
			</div>
		</template>
	</Page>
</template>

<script setup>
import Page from '@/components/Page'
import SafeAreaTop from '@/components/SafeAreaTop'
import OfferTrial7daysImage from '@/components/OfferTrial7daysImage'
import { inject, onMounted } from '@vue/runtime-core'
import { useStore } from 'vuex'
import useApi from '@/composition/api'
import { computed, ref } from '@vue/reactivity'
import PButton from '@/components/Button'
import { useI18n } from 'vue-i18n'
import Prompt from '@/components/Prompt'
import ProductTerms from '@/components/ProductTerms'
import { useRouter } from 'vue-router'
import useConfig from '@/composition/config'

const config = useConfig()
const { t } = useI18n()
const amplitude = inject('amplitude')
const store = useStore()
const api = useApi()
const isAndroid = api.getPlatform() == 'android'
const activeProduct = computed(() => {
	const { productId, trialTitle, terms, price, subtitle } = config.get({
		key: 'offerTrial',
		default_value: {
			productId: 'simple_original_subscription_12_weeks_2999_trial_7d',
			trialTitle: t('product.quarterly_with_trial_title', { count: 7 }),
			terms:
				'Following your free trial, a 12-week subscription fee of [price] will apply  and will automatically renew unless canceled. Any remaining free trial will be forfeited upon purchase. Eligible for new users only.',
			price: 'then [price]/12 weeks',
			subtitle: '[weekly_price]/week'
		}
	})

	const product = Object.assign({
		id: productId,
		text: trialTitle,
		subtitle: subtitle,
		terms: terms,
		price: price,
		preset: true
	})
	return store.getters['products/getData']([product])[0]
})
const showPaywallCancelPrompt = ref(false)
const isLoading = computed(() => store.state.isLoading)
const next = computed(() => config.get({ key: 'postPaid.type', default_value: 'workbooks' }))
const router = useRouter()

const offerTrialConfig = computed(() =>
	config.get({
		key: 'offerTrial',
		default_value: { pageTitle: 'Not sure now? <br /> Try Simple for free!', continueButtonText: 'Start free trial' }
	})
)

const isOfferTrailHasForcePaywall = computed(() =>
	config.get({ key: 'paywall.isOfferTrailHasForcePaywall', default_value: false })
)

function close(Status = 'Close', product, nextRoute) {
	amplitude.logEvent('Paywall - Close', {
		Layout: 'Offer trial',
		From: store.getters['getSource'],
		Step: 'Paywall',
		Status
	})

	amplitude.logEvent(store.getters['getAmplitudeEventName']('AddProductPaywallDone'), {
		action: Status.toLowerCase(),
		product_type: 'trial',
		...product
	})

	router.push({
		name: nextRoute || next.value,
		query: {
			Status
		}
	})
}

const points = computed(() => {
	return [
		{
			id: 1,
			text: 'Hit your goal weight'
		},
		{
			id: 2,
			text: 'Support to stay on track'
		},
		{
			id: 3,
			text: 'Feel more energetic'
		},
		{
			id: 4,
			text: 'Improved metabolic health'
		},
		{
			id: 5,
			text: 'Motivating personal insights'
		}
	]
})

function handleSubmit() {
	purchase(activeProduct.value)
}

function handleBackButtonClick() {
	router.back()
}

function purchase(product) {
	api.purchase({
		productId: product.id,
		onFinish: () => {
			amplitude.logEvent('Paywall - Done', {
				Layout: 'Offer trial',
				From: store.getters['getSource'],
				Step: 'Paywall',
				ProductId: product.id,
				Status: 'Subscribe'
			})
			close('Subscribe', {
				product_id: product.id,
				duration_month: product.duration_months,
				price: Math.abs(product.product_price),
				currency: product.currency
			})
		},
		onError: () => {
			console.log('error')
		},
		onCancel: () => {
			showPaywallCancelPrompt.value = true
		},
		closeOnSuccess: false
	})
}

function onClosePaywallCancelPrompt() {
	showPaywallCancelPrompt.value = false
}

function onActionPaywallCancelPrompt() {
	showPaywallCancelPrompt.value = false
	purchase(activeProduct.value)
}

onMounted(() => {
	amplitude.logEvent('Paywall - Show', {
		Layout: 'Offer trial',
		From: store.getters['getSource'],
		Step: 'Paywall'
	})

	amplitude.logEvent(store.getters['getAmplitudeEventName']('AddProductPaywallShow'), {
		product_type: 'trial',
		product_ids: [activeProduct.value.id],
		prices: [activeProduct.value.product_price],
		currency: activeProduct.value.currency?.toLowerCase()
	})
})
</script>

<style scoped lang="scss">
.offer-trial {
	position: relative;
	font-size: 1.145rem;

	@media (min-height: 700px) {
		max-height: 100vh;
		overflow-y: hidden;
	}

	.container.with-back-button .title {
		padding-top: 4rem;
	}
}

.title {
	padding-top: 3rem;
	font-weight: 900;
	font-size: 2rem;
	letter-spacing: -0.7px;
	margin-bottom: 3rem;
	font-family: var(--font-family-title);
}

.trial-image {
	width: 100%;
	max-width: 327px;
	height: auto;
	margin: 0 0 3rem;
}

.description {
	display: block;
	margin-bottom: 1.5rem;

	&-trial {
		text-transform: var(--offer-trial-text-transform, uppercase);
	}

	&-separator {
		font-size: 1.2rem;
		margin: 0 0.5rem;
		display: inline-block;
	}

	& > * {
		display: inline;
	}
}

.point {
	display: flex;
	align-items: center;
	margin-bottom: 1rem;

	svg {
		margin-right: 0.8rem;
	}
}

.price {
	margin-bottom: 1rem;
	font-size: 0.9rem;
	font-weight: 500;
}
</style>
